<script setup lang="ts">
import { computed, watch, ref } from 'vue';
import { useRiskDataStore } from '../../../stores/riskData';
import { storeToRefs } from 'pinia';
import {useIsFormDirty} from "vee-validate";

const model = defineModel<Record<string, any>[]>();

const props = defineProps<{
  fieldName: string;
  rules: (string | object)[];
  questionText: string;
  helpText: string;
  isNestedModel: boolean;
  isAdditionalForm: boolean;
  errorMessage: string;
  allowUpToMax?: boolean;
  maxTotal?: number;
}>();

const error = ref<string>('');
const isFormEdited = ref(false);
let initialized = false;

const allowUpToMax = computed(() => props.allowUpToMax ?? false);
const maxTotal = computed(() => props.maxTotal ?? 100);

// Get form ref for field revalidation
const riskDataStore = useRiskDataStore();
const { additionalForm, initialForm } = storeToRefs(riskDataStore);
const form = props.isAdditionalForm ? additionalForm : initialForm;

const totalNumeric = computed(() =>
    (model.value?.map(p => parseInt(p.Value || '0', 10))
        .reduce((acc, curr) => acc + (isNaN(curr) ? 0 : curr), 0)) ?? 0
);

const getTotalPercentage = (percentages: Record<string, any>[]) => {
  const total =
      percentages
          ?.map(p => parseInt(p.Value || '0'))
          .reduce((acc, curr) => acc + (isNaN(curr) ? 0 : curr), 0) ?? 0;

  return `${total}%`;
};

const totalPercentage = computed(() => `${totalNumeric.value}%`);

watch(
    () => model.value.map(o => o.Value),
    () => {
      model.value.forEach(p => {
        let v = parseInt((p?.Value ?? '0') as string, 10);
        if (isNaN(v) || v < 0) v = 0;
        p.Value = String(v);
      });
    },
    { immediate: true } // ← ensures initial zeros are set
);

watch(
    () => model.value.map(o => o.Value),
    (newVals, oldVals = []) => {
      // first run is skipped because immediate:false
      const changed = oldVals.length > 0 && newVals.some((v, i) => v !== oldVals[i]);
      if (!changed) return;

      isFormEdited.value = true;

      const total = totalNumeric.value;
      const fieldNameFor = (p: any) =>
          props.isNestedModel ? `${props.fieldName}.${p.Name}` : `${props.fieldName}${p.Name}`;

      if (allowUpToMax.value) {
        if (total <= maxTotal.value) {
          error.value = '';
          model.value.forEach(p => form.value.validateField(fieldNameFor(p)));
        } else {
          error.value = `The total percentage should be at most ${maxTotal.value}%`;
          model.value.forEach(p =>
              form.value.setFieldError(fieldNameFor(p), `The total percentage should be at most ${maxTotal.value}%`)
          );
        }
      } else {
        if (total === 100) {
          error.value = '';
          model.value.forEach(p => form.value.validateField(fieldNameFor(p)));
        } else {
          error.value = 'The total percentage should be 100%';
          model.value.forEach(p =>
              form.value.setFieldError(fieldNameFor(p), 'The total percentage should be 100%')
          );
        }
      }

      // only force revalidation after user edits
      setTimeout(() => {
        document
            .querySelectorAll(
                `.percentage-form[id='${props.fieldName}'] input[name*='${props.fieldName}'].is-invalid`
            )
            .forEach(f => form.value.validateField((f as HTMLInputElement).name));
      }, 100);
    },
    { immediate: false } // ← no validation on init
);
// is2.common.js
const numbersFilter = allowOnlyNumbers;
</script>

<template>
    <div class="fieldset">
        <div class="form-row percentage-form" :id="fieldName">
            <div class="label-container col-12">
                 <label class="form-label" v-html="questionText"></label>
                 <button v-if="helpText"
                         type="button"
                         class="label-tooltip d-flex px-0 py-2"
                         data-bs-toggle="tooltip"
                         data-bs-html="true"
                         :data-bs-title="helpText"
                         data-bs-placement="left"
                         data-bs-custom-class="custom-tooltip" tabindex="-1">
                     <img aria-hidden="true" tabindex="0" src="/assets/images/icons/info-circle.svg" class="me-1" />
                     Need help?
                 </button>
            </div>

          <div v-if="isFormEdited && error" class="validation-summary-errors mt-0">
            <b>{{ error }}</b>
          </div>

          <v-field v-for="(percentage, index) in model" :key="index"
                    :name="isNestedModel ? fieldName + '.' + percentage.Name : fieldName + percentage.Name"
                    :rules="rules"
                    :validateOnBlur="false"
                    :validateOnChange="false"
                    v-model="percentage.Value"
                    v-slot="{ field, meta, errorMessage }">
                <div class="row form-subrow">
                    <div class="text-secondary col-xs-12 col-sm-6 col-md-8 my-auto">
                        <label>{{ percentage.Text }} <span class="glyphicon total-tick glyphicon-tasks"></span></label>
                    </div>
                    <div class="col-xs-12 col-sm-6 col-md-4">
                        <div class="input-group">
                            <input v-bind="field" class="form-control form-field" tabindex="0" maxlength="3" @keydown="numbersFilter($event, field)" placeholder="%"
                                   :class="{
                                  'is-invalid': (isFormEdited && meta?.validated && !meta?.valid && parseInt(percentage.Value || '0', 10) > 0),
                                  'valid': (isFormEdited && meta?.validated && meta?.valid),
                                  'incomplete': (!isFormEdited && !meta?.validated),
                                  'dirty': !!meta?.dirty
                                }" />
                        </div>
                    </div>
                </div>
            </v-field>

            <div class="row form-subrow">
                <div class="text-secondary col-xs-12 col-sm-6 col-md-8 my-auto fw-semibold">
                    <label>Total <span class="glyphicon total-tick glyphicon-tasks"></span></label>
                </div>
                <div class="col-xs-12 col-sm-6 col-md-4">
                    <div class="input-group">
                      <input
                          :value="totalPercentage"
                          class="disabled form-control form-field"
                          :class="{
                             'is-invalid': isFormEdited && (allowUpToMax ? totalNumeric > maxTotal : totalNumeric !== 100)
                          }"
                          :max="allowUpToMax ? maxTotal : 100"
                          readonly
                          tabindex="-1"
                          onclick="return false"
                          onfocus="return false"
                          oncontextmenu="return false"
                          onkeydown="return false"
                      />

                      <small v-if="allowUpToMax" class="text-muted mt-2">
                        You can enter any mix that totals up to {{ maxTotal }}%.
                      </small>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>


